import React from "react";
import cn from "./helpers/cn";

export type TypographyVariant =
  | "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  | "subtitle1" | "subtitle2"
  | "body1" | "body2"
  | "button" | "caption" | "overline"
  | "srOnly" | "inherit";

export type TypographyAlign = "inherit" | "left" | "center" | "right" | "justify";

export type TypographyColor =
  | "initial" | "inherit"
  | "primary" | "secondary"
  | "textPrimary" | "textSecondary"
  | "error";

export type TypographyDisplay = "initial" | "block" | "inline";

interface TypographyProps extends Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  variant?: TypographyVariant;
  align?: TypographyAlign;
  color?: TypographyColor;
  display?: TypographyDisplay;
  gutterBottom?: boolean;
  noWrap?: boolean;
  paragraph?: boolean;
  component?: React.ElementType;
  variantMapping?: Partial<Record<TypographyVariant, React.ElementType>>;
}

const defaultVariantMapping: Record<TypographyVariant, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  button: "span",
  caption: "span",
  overline: "span",
  srOnly: "span",
  inherit: "p",
};

const variantClasses: Record<TypographyVariant, string> = {
  h1: "text-6xl font-light leading-[1.167] tracking-tight",
  h2: "text-5xl font-light leading-[1.2] tracking-tight",
  h3: "text-[3rem] font-normal leading-[1.167]",
  h4: "text-[2.125rem] font-normal leading-[1.235]",
  h5: "text-2xl font-normal leading-[1.334]",
  h6: "text-xl font-medium leading-[1.6]",
  subtitle1: "text-base font-normal leading-[1.75]",
  subtitle2: "text-sm font-medium leading-[1.57]",
  body1: "text-base font-normal leading-[1.5]",
  body2: "text-sm font-normal leading-[1.43]",
  button: "text-sm font-medium leading-[1.75] uppercase tracking-[0.02857em]",
  caption: "text-xs font-normal leading-[1.66] tracking-[0.03333em]",
  overline: "text-xs font-normal leading-[2.66] tracking-[0.08333em] uppercase",
  srOnly: "sr-only",
  inherit: "",
};

const alignClasses: Record<TypographyAlign, string> = {
  inherit: "",
  left: "text-left",
  center: "text-center",
  right: "text-right",
  justify: "text-justify",
};

const colorClasses: Record<TypographyColor, string> = {
  initial: "",
  inherit: "text-inherit",
  primary: "text-primary",
  secondary: "text-secondary",
  textPrimary: "primary-tx",
  textSecondary: "secondary-tx",
  error: "text-red-600",
};

const displayClasses: Record<TypographyDisplay, string> = {
  initial: "",
  block: "block",
  inline: "inline",
};

const Typography = React.forwardRef<HTMLElement, TypographyProps>((props, ref) => {
  const {
    variant = "body1",
    align = "inherit",
    color = "initial",
    display = "initial",
    gutterBottom = false,
    noWrap = false,
    paragraph = false,
    component,
    variantMapping,
    className,
    children,
    ...other
  } = props;

  const Component =
    component ??
    (paragraph ? "p" : (variantMapping?.[variant] ?? defaultVariantMapping[variant]));

  return (
    <Component
      ref={ref as React.Ref<HTMLElement>}
      className={cn(
        "m-0",
        variantClasses[variant],
        alignClasses[align],
        colorClasses[color],
        displayClasses[display],
        noWrap && "overflow-hidden text-ellipsis whitespace-nowrap",
        gutterBottom && "mb-[0.35em]",
        paragraph && "mb-4",
        className
      )}
      {...other}
    >
      {children}
    </Component>
  );
});

Typography.displayName = "Typography";

export default Typography;
